import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// Deep-link helpers: keep view state (filters, tabs, layouts, scope, search) in
// the URL query string so forward/back and sharing work. State is written with
// { replace: true } so typing in a filter doesn't spam the history stack.
//
// Writes go through navigate() rather than setSearchParams for one reason:
// setSearchParams rebuilds the URL from the params alone and DROPS the hash,
// which killed a #task-<id> permalink the moment a remedy cleared the filter
// that hid it (measured 2026-08-26 on /my-tasks — the row appeared, unmarked,
// and the fragment was gone). The hash is view state too; preserve it.

/** A single string param, with a default that is omitted from the URL. */
export function useUrlState(key: string, def = ""): [string, (v: string) => void] {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const value = sp.get(key) ?? def;
  const set = useCallback(
    (v: string) => {
      const n = new URLSearchParams(sp);
      if (v == null || v === "" || v === def) n.delete(key);
      else n.set(key, v);
      const search = n.toString();
      navigate({ pathname, search: search ? `?${search}` : "", hash }, { replace: true });
    },
    [key, def, sp, pathname, hash, navigate]
  );
  return [value, set];
}

/** A comma-separated list param (e.g. multi-select scope / filters). */
export function useUrlList(key: string): [string[], (v: string[]) => void] {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const raw = sp.get(key);
  const value = raw ? raw.split(",").filter(Boolean) : [];
  const set = useCallback(
    (v: string[]) => {
      const n = new URLSearchParams(sp);
      if (!v || v.length === 0) n.delete(key);
      else n.set(key, v.join(","));
      const search = n.toString();
      navigate({ pathname, search: search ? `?${search}` : "", hash }, { replace: true });
    },
    [key, sp, pathname, hash, navigate]
  );
  return [value, set];
}

/**
 * Write several params in ONE navigation. Setting each key through its own
 * hook fires one navigation per key, and same-tick calls all compute from the
 * same starting location — the last write silently wins and the rest vanish.
 * (Found 2026-08-26: /tasks' setFilters wrote nine params and the URL stayed
 * clean.) Empty string / empty array deletes the key.
 */
export function useUrlParams(): (entries: Record<string, string | string[]>) => void {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  return useCallback(
    (entries) => {
      const n = new URLSearchParams(sp);
      for (const [k, v] of Object.entries(entries)) {
        const empty = Array.isArray(v) ? v.length === 0 : v === "";
        if (empty) n.delete(k);
        else n.set(k, Array.isArray(v) ? v.join(",") : v);
      }
      const search = n.toString();
      navigate({ pathname, search: search ? `?${search}` : "", hash }, { replace: true });
    },
    [sp, pathname, hash, navigate]
  );
}
